import type { NotificationPublic, ReadNotificationsType } from '@repo/types/notifications';
import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { format, parseISO } from 'date-fns';
import { Dot, LinkIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';

import { useNotificationsSheet } from '@/components/features/notifications/NotificationSheetProvider';
import NotificationTypeBadge from '@/components/features/notifications/NotificationTypeBadge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Props for NotificationCard component.
 * @property {(id: number) => void} makeRead - Callback to mark notification as read.
 * @property {NotificationPublic} notification - Notification data to display.
 * @property {...ComponentProps<'div'>} - Additional div props.
 */
interface NotificationCardProps extends ComponentProps<'div'> {
  makeRead: (data: ReadNotificationsType) => void;
  notification: NotificationPublic;
}

export default function NotificationCard(notificationCardProps: NotificationCardProps) {
  const { makeRead, notification, className, ...props } = notificationCardProps;
  const { onOpenChange } = useNotificationsSheet();

  const handleCloseSheet = () => {
    onOpenChange(false);
  };

  const handleRead = () => {
    makeRead({
      id: notification.id,
      type: notification.type,
      entityId: notification.entityId,
    });
  };

  const isExternalUrl = useMemo<boolean>(() => {
    if (!notification.url) return false;
    return notification.url.startsWith('http://') || notification.url.startsWith('https://');
  }, [notification.url]);

  return (
    <div
      data-read={notification.read}
      className={cn(
        'group/notification-card border-border-primary bg-card relative space-y-3 rounded-md border p-2 shadow-xs duration-150',
        notification.read && 'bg-transparent opacity-50 shadow-none',
        className
      )}
      {...props}
    >
      <header className={'flex flex-col gap-1'}>
        <NotificationTypeBadge type={notification.type} />
        <h5 className={cn('text-md mr-8 truncate', notification.read ? 'font-medium' : 'font-semibold')}>{notification.name}</h5>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button onClick={handleRead} className={cn('absolute top-0.5 right-0.5', notification.read && 'invisible opacity-0')} size={'sm'} variant={'ghost'}>
              <Dot className={'text-brand stroke-[10px]'} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">Oznacz jako przeczytane</TooltipContent>
        </Tooltip>
      </header>
      <div className={'flex flex-col gap-1'}>
        {notification.url ? (
          isExternalUrl ? (
            <a href={notification.url} target="_blank" rel="noopener noreferrer" className="text-brand flex w-fit cursor-pointer items-center gap-1 text-sm hover:underline">
              <LinkIcon className="size-3.5" />
              Link
            </a>
          ) : (
            <Link
              to={notification.url}
              className="text-brand flex w-fit cursor-pointer items-center gap-1 text-sm hover:underline"
              onClick={() => {
                handleRead();
                handleCloseSheet();
              }}
            >
              <LinkIcon className="size-3.5" />
              Link
            </Link>
          )
        ) : null}
        <p className={'text-accent-foreground line-clamp-4 text-sm text-pretty'}>{notification.content}</p>
      </div>
      <footer className={'text-muted-foreground flex justify-between'}>
        <p className={'text-xs'}>{format(parseISO(notification.createdAt), 'EEEE H:mm')}</p>
        <p className={'text-xs'}>{format(parseISO(notification.createdAt), 'MMM d, yyyy')}</p>
      </footer>
    </div>
  );
}
